import { ApiError, statusToCode } from '@/lib/api/errors';

/**
 * Query key for the public quote's payment-plan preview.
 *
 * Shared on purpose. The key is written in the route's `useQuery` and read
 * again ~400 lines away when the signed PDF is re-rendered after approval. If
 * those two drifted, the read would silently miss, fall back to the stale
 * pre-reprice preview, and reintroduce exactly the mismatch the re-render
 * exists to remove — with no error anywhere.
 */
export function publicPaymentPlanKey(
  id: string,
  acceptMembershipOffer = false,
  membershipBillingOptionId?: string | null,
) {
  return [
    'public',
    'estimate',
    id,
    'payment-plan',
    acceptMembershipOffer,
    membershipBillingOptionId ?? null,
  ] as const;
}

/**
 * Fetch the payment-plan preview from the server.
 *
 * The public quote uses this both for its normal query and for the signed-PDF
 * refresh after approval. Keeping the request in one place prevents the PDF
 * path from trusting a stale cache entry when the broad estimate refetch
 * fails. A missing plan is authoritative (`204`/empty `200`); other failures
 * must reject so callers cannot silently mix fresh estimate totals with old
 * installment stages.
 */
export async function fetchPublicPaymentPlanPreview<T>(params: {
  operationsUrl: string;
  id: string;
  tenantId: string;
  acceptMembershipOffer?: boolean;
  membershipBillingOptionId?: string | null;
}): Promise<T | null> {
  const search = new URLSearchParams();
  if (params.acceptMembershipOffer) search.set('acceptMembershipOffer', 'true');
  if (params.membershipBillingOptionId) {
    search.set('membershipBillingOptionId', params.membershipBillingOptionId);
  }
  const membershipQuery = search.size > 0 ? `?${search.toString()}` : '';
  const response = await fetch(
    `${params.operationsUrl}/estimates/public/${params.id}/schedule-template/preview${membershipQuery}`,
    { headers: { 'x-tenant-id': params.tenantId } },
  );
  if (response.status === 204) return null;
  if (!response.ok) {
    // An ApiError, not a bare Error: it carries the status, so a 4xx — the
    // estimate went back to draft while the customer had the page open, or
    // the link is dead — is the server's verdict and stays out of the RUM
    // JS-error rate (captureQueryError), exactly like every axios 4xx.
    throw new ApiError(
      response.status,
      statusToCode(response.status),
      `Could not refresh payment-plan preview (${response.status})`,
    );
  }
  const body = await response.text();
  return body ? (JSON.parse(body) as T) : null;
}
